import { toast } from 'react-toastify';

import type { DrugFormType } from '@/features/customization/schema';
import type { MedicationDrugType } from '@/features/prescription/medication-drug/schema';
import { type MedicationDrugFormType } from '@/features/prescription/medication-drug/schema';
import { CONCEPT_ATTRIBUTE_TYPES, DEFAULT_DRUG_VALUES, EMPTY_RESULT, STORAGE_KEYS } from '@/shared/constants';
import { SEPARATORS } from '@/shared/constants/medication';
import { PrescriptiopayloadHelpers } from '@/shared/constants/prescription';
import type { DrugPayload, DrugUnitsKey, OrderAction } from '@/shared/types';
import type { Order } from '@/shared/types/order';
import type {
  CreateEmptyMedicationProps,
  DrugOrder,
  MedicationPayload,
  OrderReason,
  SuggestedMedicine,
  Units,
} from '@/shared/types/prescription/medication';
import { isJsonString, isNewRow, isRowSaved, roundUptoTwoDecimals } from '@/shared/utils/helpers';
import { countActiveDoseSlots, extractLongDisplay, extractShortDisplay } from '@/shared/utils/prescription/medication';
import { logger, setSessionStorage } from '@indi/utils';
import { add, differenceInDays, startOfDay } from 'date-fns';

interface DrugOrderData {
  currentMedications: DrugOrder[];
  pastDrugOrder: any;
  drugOrder: any[];
}

const customStyles = {
  menu: (provided: any) => ({
    ...provided,
    width: 'auto',
    minWidth: '100%',
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999, pointerEvents: 'auto' }),
  option: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => {
    if (data.isSeparator) {
      return {
        ...styles,
        'backgroundColor': 'transparent',
        'borderTop': '2px solid #0284C7',
        'borderBottom': '2px solid #0284C7',
        'padding': '4px 12px',
        'margin': '4px 0',
        'fontSize': '0.8em',
        'color': '#666',
        'fontStyle': 'italic',
        'cursor': 'default',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      };
    }

    return {
      ...styles,
      backgroundColor: isDisabled ? undefined : isSelected ? '#0066cc' : isFocused ? '#deebff' : undefined,
      color: isDisabled ? '#ccc' : isSelected ? 'white' : 'black',
      cursor: isDisabled ? 'not-allowed' : 'default',
    };
  },
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '12px',
    color: '#000000',
    backgroundColor: state.isDisabled ? '#f0f0f0' : '#ffffff',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: '#000000',
  }),
  input: (provided: any) => ({
    ...provided,
    color: '#000000',
  }),
};

export const drugStyles = {
  ...customStyles,
  control: (provided: any) => ({
    ...provided,
    'textTransform': 'uppercase !important',
    '& input': {
      textTransform: 'uppercase !important',
    },
  }),
  singleValue: (provided: any) => ({
    ...provided,
    'textTransform': 'uppercase !important',
    '& input': {
      textTransform: 'uppercase !important',
    },
  }),
  input: (provided: any) => ({
    ...provided,
    'textTransform': 'uppercase !important',
    '& input': {
      textTransform: 'uppercase !important',
    },
  }),
};

const createDefaultStrength = () => ({
  numerator: { value: '', unit: 'mg' },
  denominator: { value: '5', unit: 'ml' },
});

export const getDefaultUnit = (units: Array<any>, defaultValue: string) => {
  const unit = units?.find(u => u.value === defaultValue);
  return {
    value: unit?.value || '',
    label: unit?.label || '',
  };
};

const getDefaultDrugValues = (
  frequencyUnits: Array<Units>,
  dosageUnits: Array<Units>,
  routesList: Array<Units>,
  duratinUnitsList: Array<Units>,
  dosingInstruction: Array<Units>,
  drugFormsList: Array<Units>,
) => {
  return {
    frequency: getDefaultUnit(frequencyUnits, DEFAULT_DRUG_VALUES.FREQUENCY),
    dose: getDefaultUnit(dosageUnits, DEFAULT_DRUG_VALUES.DOSE),
    route: getDefaultUnit(routesList, DEFAULT_DRUG_VALUES.ROUTE),
    duration: getDefaultUnit(duratinUnitsList, DEFAULT_DRUG_VALUES.DURATION),
    dosingInstruction: getDefaultUnit(dosingInstruction, DEFAULT_DRUG_VALUES.DOSING_INSTRUCTION),
    dosageForm: getDefaultUnit(drugFormsList, DEFAULT_DRUG_VALUES.DOSAGE_FORM),
  };
};

const normalizeDrugOrder = (
  order: Order,
  units: Record<DrugUnitsKey, Units[]>,
  recentVisitDrugOrders?: Order[],
  hasActiveEncounter: boolean = true,
): DrugOrder => {
  try {
    const orderReason = isJsonString(order?.orderReasonNonCoded || '{}')
      ? (JSON.parse(order?.orderReasonNonCoded!) as OrderReason)
      : ({} as OrderReason);
    const orderReasonNonCoded = orderReason?.orderReasonNonCoded;
    const comment = orderReason?.comment;

    const frequencyUnit = units?.frequency?.find(
      frequency => extractLongDisplay(frequency?.label) === order?.frequency?.display,
    );
    const isRecent = recentVisitDrugOrders?.find(i => i?.uuid === order?.uuid);

    const defaults = getDefaultDrugValues(
      units.frequency,
      units.dosage,
      units.routes,
      units.duration,
      units.instruction,
      units.drugForms,
    );

    // 🧩 Duration recalculation for UI
    const scheduledDate = order?.scheduledDate ? new Date(order.scheduledDate) : null;
    const now = new Date();

    const durationValue = Number(order?.duration || 0);
    const durationUnit = order?.durationUnits?.display || '';

    let displayDuration = durationValue;

    const isPastScheduledDate = scheduledDate && scheduledDate < startOfDay(now);
    if (isPastScheduledDate) {
      const pastDays = Math.max(differenceInDays(now, scheduledDate), 0);
      const prescribedDays = durationValue * (durationUnitToDays[durationUnit] || 1);

      // 🔹 We show only prescribedDays part in UI
      // (since totalDuration was already saved)
      const remainingDays = Math.max(prescribedDays - pastDays, 0);

      displayDuration = remainingDays;
    }

    const dose = order?.instructions && isFreeTextDose(order?.instructions) ? order?.instructions : order?.dose;
    return {
      action: hasActiveEncounter ? order?.action : 'REVISE',
      accessionNumber: order?.accessionNumber ?? '',
      autoExpireDate: order?.autoExpireDate,
      concept: {
        uuid: order?.drug?.concept?.uuid ?? order?.concept?.uuid,
        display: order?.drug?.concept?.display ?? order?.concept?.display,
      },
      comment: comment,
      careSetting: {
        uuid: order?.careSetting?.uuid,
        display: order?.careSetting?.display!,
      },
      dateActivated: order?.dateActivated,
      drug: {
        uuid: order?.drug?.uuid,
        display: order?.drug?.display?.toUpperCase(),
      },
      dosageForm: {
        uuid: order?.drug?.dosageForm?.uuid ?? defaults?.dosageForm?.value,
        display: order?.drug?.dosageForm?.display ?? defaults?.dosageForm?.label,
      },
      dose: dose ?? 1,
      doseUnits: {
        uuid: order?.doseUnits?.uuid ?? defaults?.dose?.value,
        display: order?.doseUnits?.display ?? defaults?.dose?.label,
      },
      dosingInstruction: {
        uuid: order?.dosingInstructions ? '' : defaults?.dosingInstruction?.value,
        display: order?.dosingInstructions ? order?.dosingInstructions! : defaults?.dosingInstruction?.label,
      },
      duration: displayDuration,
      durationUnits: {
        uuid: order?.durationUnits?.uuid ?? defaults.duration?.value,
        // if isPastScheduledDate, then duration label should be 'Days' only,
        // since we are re-calculating the duration for past scheduled date in 'Days' only
        display: isPastScheduledDate
          ? defaults.duration?.label
          : (order?.durationUnits?.display ?? defaults.duration?.label),
      },
      encounter: {
        uuid: order?.encounter?.uuid,
        display: order?.encounter?.display,
      },
      frequency: {
        uuid: order?.frequency?.uuid ?? defaults?.frequency?.value,
        display: frequencyUnit?.label ?? order?.frequency?.display ?? defaults?.frequency?.label,
      },
      generic: {
        uuid: order?.drug?.concept?.uuid ?? order?.concept?.uuid,
        display: order?.drug?.concept?.display ?? order?.concept?.display,
      },
      isNew: false,
      orderReasonNonCoded: orderReasonNonCoded?.length > 0 ? orderReasonNonCoded : EMPTY_RESULT,
      orderer: {
        uuid: order?.orderer?.uuid,
        display: order?.orderer?.display,
      },
      quantityUnits: {
        uuid: order?.quantityUnits?.uuid ?? defaults?.dose?.value,
        display: order?.quantityUnits?.display ?? defaults?.dose?.label,
      },
      patient: {
        uuid: order?.patient?.uuid,
        display: order?.patient?.display,
      },
      quantity: order?.quantity ?? 1,
      recentDrug: Boolean(isRecent?.uuid?.length),
      route: {
        uuid: order?.route?.uuid ?? defaults?.route?.value,
        display: order?.route?.display ?? defaults?.route?.label,
      },
      strength: order?.drug?.strength ?? '1',
      scheduledDate: hasActiveEncounter ? (order?.scheduledDate ?? order?.dateActivated) : new Date().toISOString(),
      urgency: order?.urgency ?? '',
      uuid: order?.uuid,
      status: hasActiveEncounter ? 'saved' : 'new',
      instructions: order?.instructions ?? '',
    };
  } catch (error) {
    logger.error({
      tag: 'Medication',
      message: 'Error normalizing drug order:',
      error,
      metadata: { order },
    });
    throw new Error('Failed to normalize drug order');
  }
};

const formatMedicationsForSuggestion = (medications: Array<DrugOrder>) => {
  return medications?.map(
    (medication: DrugOrder) =>
      `The patient was taking ${medication.drug.display} for past ${medication.duration} ${medication.durationUnits.display}`,
  );
};

const storeMedicationToStorage = (value: any) => setSessionStorage(STORAGE_KEYS.MEDICATIONS, value);

const durationUnitToDays: Record<string, number> = {
  Days: 1,
  days: 1,
  Weeks: 7,
  weeks: 7,
  Months: 30,
  months: 30,
  Years: 365,
  years: 365,
};
const extractStrengthValues = (value: string): string => {
  const matches = value?.match(/\((.*?)\)/g);
  if (!matches) return value;

  return matches.map(match => match.replace(/[()]/g, '').trim()).join(' + ');
};

const isValidStrength = (strength: string): boolean => {
  // Supports: "1 mg", "250mcg", "1 mg/5 ml", etc.
  const singleStrength = /^[0-9]+(\.[0-9]+)?\s*[a-zA-Z]+$/;
  const ratioStrength = /^[0-9]+(\.[0-9]+)?\s*[a-zA-Z]+\s*\/\s*[0-9]+(\.[0-9]+)?\s*[a-zA-Z]+$/;
  return singleStrength.test(strength.trim()) || ratioStrength.test(strength.trim());
};

const extractTextInBrackets = (value: string): string | null => {
  const match = value.match(/\(([^)]+)\)/);
  return match ? match[1].trim() : null;
};

const findUuidByLabel = (data: Array<any>, label: string) => {
  return data?.find(i => i.label === label);
};

const findConceptByDrug = (medications: Array<any>, drug: string) => {
  return medications?.find((medicine: any) => medicine?.display === drug);
};

const findDuplicateMedicine = (currMedicines: Array<any>, selectedMedicine: string) => {
  return currMedicines?.some((medicine: any) => medicine?.drug?.display === selectedMedicine);
};

const isMedicationExpired = (row: MedicationDrugFormType) => {
  const autoExpireDate = new Date(row.autoExpireDate!);
  // const isRecentDrug = row?.recentDrug;
  const isExpired = autoExpireDate < new Date();
  const isEmpty = !row?.drug?.display;

  if (isEmpty) return '';
  return isExpired ? true : false;
};

const formatDrugOrder = (drugData: any, isRecent: any) => {
  const result = drugData?.map((drug: any) => ({
    ...drug,
    customDrugConcept: '',
    customDrugConceptuuid: '',
    customGenericConceptuuid: drug?.concept?.uuid ?? drug?.drug?.concept?.uuid,
    concept: drug?.concept ?? drug?.drug?.concept,
    dosageForm: {
      uuid: drug?.drug?.dosageForm?.uuid,
      display: drug?.drug?.dosageForm?.display,
    },
    doseUnitName: drug?.doseUnits?.display,
    doseUnits: drug?.doseUnits?.uuid,
    DosingInstruction: drug?.dosingInstructions,
    dosingInstructions: drug?.dosingInstructions,
    drugconcept: drug?.concept,
    drug: drug?.drug?.uuid,
    drugName: drug?.drug?.display.replace('(NEW)', '').trim(),
    druguuid: drug?.drug?.uuid,
    durationUnitName: drug?.durationUnits?.display,
    frequency: drug?.frequency?.uuid,
    frequencyName: drug?.frequency?.display,
    genericName: drug?.concept?.display ?? drug?.drug?.concept?.display,
    quantityUnitName: drug?.quantityUnits?.display,
    quantityUnits: drug?.quantityUnits?.uuid,
    routeName: drug?.route?.display,
    showSearchBox: false,
    showaddnewdrug: false,
    shwoGenericBox: false,
    strength: drug?.drug?.strength,
    recentDrug: isRecent ? true : false,
  }));
  return result;
};

const prepareMedicationPayload = ({
  action,
  data,
  providerUuid,
  encounterUuid,
  patientUuid,
  orderReason,
}: {
  action: OrderAction;
  data: MedicationDrugFormType;
  providerUuid?: string;
  encounterUuid?: string;
  patientUuid?: string;
  orderReason?: any;
}): MedicationPayload => {
  const doseValue = getDoseValue(data?.dose);

  if (action === 'NEW') {
    return {
      action,
      accessionNumber: data.accessionNumber ?? '',
      patient: patientUuid!,
      type: 'drugorder',
      careSetting: PrescriptiopayloadHelpers?.careSetting,
      orderer: providerUuid!,
      encounter: encounterUuid!,
      drug: { uuid: data?.drug?.uuid! },
      dose: doseValue,
      doseUnits: data?.doseUnits?.uuid,
      route: data?.route?.uuid,
      frequency: data?.frequency?.uuid,
      numRefills: 1,
      quantity: data?.quantity,
      quantityUnits: data?.quantityUnits?.uuid,
      duration: data?.duration,
      durationUnits: data?.durationUnits?.uuid,
      dosingType: 'org.openmrs.SimpleDosingInstructions',
      dosingInstructions: data?.dosingInstruction?.display,
      concept: data?.concept?.uuid,
      orderReasonNonCoded: JSON.stringify(orderReason),
      scheduledDate: data?.scheduledDate ?? new Date().toISOString(),
      urgency: 'ON_SCHEDULED_DATE',
      instructions: data?.instructions ?? '',
    };
  } else if (action === 'REVISE') {
    return {
      action,
      patient: patientUuid!,
      accessionNumber: data.accessionNumber ?? '',
      type: 'drugorder',
      careSetting: PrescriptiopayloadHelpers?.careSetting,
      orderer: providerUuid!,
      encounter: encounterUuid!,
      drug: { uuid: data?.drug?.uuid! },
      dose: doseValue,
      doseUnits: data?.doseUnits?.uuid,
      route: data?.route?.uuid,
      frequency: data?.frequency?.uuid,
      numRefills: 1,
      quantity: data?.quantity,
      quantityUnits: data?.quantityUnits?.uuid,
      duration: data?.duration,
      durationUnits: data?.durationUnits?.uuid,
      dosingType: 'org.openmrs.SimpleDosingInstructions',
      dosingInstructions: data?.dosingInstruction?.display,
      concept: data?.concept?.uuid,
      orderReasonNonCoded: JSON.stringify(orderReason),
      previousOrder: data?.uuid,
      scheduledDate: data?.scheduledDate ?? new Date().toISOString(),
      urgency: 'ON_SCHEDULED_DATE',
      instructions: data?.instructions ?? '',
    };
  } else if (action === 'DISCONTINUE') {
    return {
      action,
      patient: data?.patient?.uuid!,
      type: 'drugorder',
      careSetting: data?.careSetting?.uuid!,
      orderer: data?.orderer?.uuid!,
      encounter: data.encounter?.uuid!,
      concept: data?.concept?.uuid,
      previousOrder: data?.uuid,
      drug: { uuid: data?.drug?.uuid! },
      orderReasonNonCoded: JSON.stringify(orderReason),
      duration: data?.duration,
      durationUnits: data?.durationUnits?.uuid,
      quantity: data?.quantity,
      quantityUnits: data?.quantityUnits?.uuid,
      dosingInstructions: data?.dosingInstruction?.display,
      dose: doseValue,
      doseUnits: data?.doseUnits?.uuid,
      route: data?.route?.uuid,
      frequency: data?.frequency?.uuid,
      instructions: data?.instructions,
    };
  } else throw new Error(`Unsupported medication action: ${action}`);
};

const prepareCustomGenericPayload = (data: MedicationDrugFormType, attributeUUID?: string) => {
  const genericDescriptions = data?.genericDescriptions ?? '';
  return {
    names: [
      {
        name: data.generic?.display,
        locale: 'en',
        localePreferred: true,
        conceptNameType: 'FULLY_SPECIFIED',
      },
    ],
    datatype: 'N/A',
    conceptClass: 'Drug',
    version: '1.0',
    attributes:
      genericDescriptions?.length > 0
        ? [
            {
              uuid: attributeUUID,
              attributeType: {
                uuid: CONCEPT_ATTRIBUTE_TYPES.DRUG_INFORMATION,
              },
              value: genericDescriptions,
            },
          ]
        : [],
  };
};

const prepareCustomDrugPayload = (data: MedicationDrugFormType): DrugPayload => {
  return {
    concept: data?.concept?.uuid!,
    combination: data?.combination ?? false, // Use the combination value from FORM
    name: data?.drug?.display.trim(),
    strength: data?.strength!,
    dosageForm: data?.dosageForm?.uuid!,
  };
};

const formattedFrequencyOptions = (options: Array<any>) => {
  const frequencyOptions = options?.map((item: any) => {
    return {
      ...item,
    };
  });
  return frequencyOptions;
};

const isEmptyValue = (display: unknown) => {
  return typeof display === 'string' && display === '';
};

const createEmptyMedication = ({
  frequencyUnits,
  dosageUnits,
  routesList,
  duratinUnitsList,
  dosingInstruction,
  drugFormsList,
  defaultDuration,
  calculateQuantity,
}: CreateEmptyMedicationProps): MedicationDrugFormType => {
  const hasDefaultDuration = defaultDuration.value !== '';
  const defaults = getDefaultDrugValues(
    frequencyUnits,
    dosageUnits,
    routesList,
    duratinUnitsList,
    dosingInstruction,
    drugFormsList,
  );

  return {
    uuid: '',
    action: 'NEW',
    combination: false,
    drug: {
      uuid: '',
      display: '',
    },
    generic: {
      uuid: '',
      display: '',
    },
    concept: {
      uuid: '',
      display: '',
    },
    dosageForm: {
      uuid: defaults?.dosageForm?.value || '',
      display: defaults?.dosageForm?.label || '',
    },
    dose: 1,
    doseUnits: {
      uuid: defaults?.dose?.value || '',
      display: defaults?.dose?.label || '',
    },
    frequency: {
      uuid: defaults?.frequency?.value || '',
      display: defaults?.frequency?.label || '',
    },
    careSetting: {
      uuid: '',
      display: '',
    },
    route: {
      uuid: defaults?.route?.value || '',
      display: defaults?.route?.label || '',
    },
    dosingType: 'org.openmrs.SimpleDosingInstructions',
    dosingInstruction: {
      uuid: defaults?.dosingInstruction?.value || '',
      display: defaults?.dosingInstruction?.label || '',
    },
    asNeeded: false,
    asNeededCondition: '',
    duration: hasDefaultDuration ? parseInt(defaultDuration.value) : 1,
    durationUnits: {
      uuid: hasDefaultDuration ? defaultDuration.unit.uuid : (defaults?.duration?.value ?? ''),
      display: hasDefaultDuration ? defaultDuration.unit.display : (defaults?.duration?.label ?? ''),
    },
    quantity: hasDefaultDuration
      ? calculateQuantity(1, defaults?.frequency?.label, parseInt(defaultDuration.value), defaultDuration.unit.display)
      : 1,
    quantityUnits: {
      uuid: defaults?.dose?.value || '',
      display: defaults?.dose?.label || '',
    },
    numRefills: 0,
    strength: '1',
    comment: '',
    orderReasonNonCoded: '',
    dateActivated: new Date().toISOString(),
    orderer: {
      uuid: '',
      display: '',
    },
    isNew: true,
    status: 'new',
  };
};

const getActiveDrugOrders = (drugOrder: any[]) => {
  const allDrugOrderData = formatDrugOrder(drugOrder, false);
  const activeDrugOrders = allDrugOrderData?.filter((item: any) => new Date(item?.autoExpireDate) >= new Date()); // get all active drug orders
  return activeDrugOrders?.filter((item: any) => !item?.dateStopped);
};

const combineDrugOrders = ({ currentMedications, pastDrugOrder, drugOrder }: DrugOrderData) => {
  const allVisits = Object.values(pastDrugOrder || {});
  const recentVisit = allVisits[0] as any[];

  const filteredRecentVisit = recentVisit?.filter((item: any) => !item?.dateStopped);
  const formatedRecentVisit = formatDrugOrder(filteredRecentVisit, true);
  const activeDrugOrders = getActiveDrugOrders(drugOrder);

  const uniqueItemsMap = new Map<string, any>();
  [...(currentMedications || []), ...(formatedRecentVisit || []), ...(activeDrugOrders || [])]
    .filter(Boolean)
    .forEach((item: any) => {
      uniqueItemsMap.set(item.druguuid, item);
    });

  return { uniqueItemsMap, filteredRecentVisit, formatedRecentVisit };
};

const formatSuggestedMedication = (med: any): SuggestedMedicine => {
  const genericSeparator = SEPARATORS.find(sep => med?.genericName?.includes(sep));

  const strengthSeparator = genericSeparator ? SEPARATORS.find(sep => med?.dosage?.includes(sep)) : '';

  let formattedDosage = med?.dosage;
  if (genericSeparator && strengthSeparator) {
    const genSplit = med?.genericName?.split(genericSeparator);
    const strenSplit = med?.dosage?.split(strengthSeparator);
    if (genSplit?.length === strenSplit?.length) {
      formattedDosage = genSplit
        .map((gen: string, index: number) => {
          const strength = strenSplit[index].trim();
          const genericName = gen.trim();
          return `${genericName} ( ${strength} )`;
        })
        .join(` ${genericSeparator} `);
    }
  }

  return {
    dosageForm: med?.form,
    strength: formattedDosage,
    drug: med?.brandName + ' ' + med?.dosage,
    generic: `${med?.genericName}`,
    duration: parseInt(med?.duration?.split(' ')?.[0]),
    frequency: med?.frequency,
  };
};

// eslint-disable-next-line no-useless-escape
const isInvalidGeneric = (value: string) => !/^[A-Za-z0-9\s'\+\-\&\/]+$/.test(value);

/**
 * Find the mapping key (UUID) for a given dosage form.
 * params
 * dosageForm - The display name of the dosage form
 * mappings - The drug form mappings object
 * dosageFormUuid - The UUID of the dosage form
 * returns The UUID key if found in mappings, null otherwise
 */
const findDosageMappings = (
  _dosageForm: string | undefined,
  mappings: DrugFormType,
  dosageFormUuid?: string,
): string | null => {
  if (!mappings || !dosageFormUuid) return null;

  if (mappings[dosageFormUuid]) {
    return dosageFormUuid;
  }

  return null;
};

const sortByFrequency = (frequencyUnits: Units[], medications: any[]) => {
  return [...medications].sort((a, b) => {
    const formattedFrequencies = frequencyUnits?.map(unit => ({
      ...unit,
      label: extractShortDisplay(unit?.label?.toUpperCase()),
    }));
    const freqA = extractShortDisplay(a?.frequency?.display?.toUpperCase());
    const freqB = extractShortDisplay(b?.frequency?.display?.toUpperCase());

    const indexA = formattedFrequencies.findIndex(unit => unit?.label?.toUpperCase() === freqA);

    const indexB = formattedFrequencies.findIndex(unit => unit?.label?.toUpperCase() === freqB);

    const priorityA = indexA === -1 ? frequencyUnits?.length : indexA;
    const priorityB = indexB === -1 ? frequencyUnits?.length : indexB;

    return priorityA - priorityB;
  });
};

// Accession assigning algorithm
const calculateAccessionNumber = (medications: MedicationDrugType['medications'], currentIndex: number) => {
  // avoid filtering new rows in left array, to ensure that newly added row also has accessionNumber
  const savedBefore = medications
    .slice(0, currentIndex)
    // .filter(order => isRowSaved(order?.status))
    .map(order => Number(order.accessionNumber))
    .sort((a, b) => a - b);

  const savedAfter = medications
    .slice(currentIndex + 1)
    .filter(order => isRowSaved(order?.status))
    .map(order => Number(order.accessionNumber))
    .sort((a, b) => a - b);

  let accessionNumber;

  if (savedBefore.length === 0 && savedAfter.length === 0) {
    // No saved medications, start with 1
    accessionNumber = 1;
  } else if (savedBefore.length === 0) {
    // Insert at beginning
    const minAfter = Math.min(...savedAfter);
    accessionNumber = Math.max(1, minAfter - 1);
  } else if (savedAfter.length === 0) {
    // Insert at end
    const maxBefore = Math.max(...savedBefore);
    accessionNumber = maxBefore + 1;
  } else {
    // Insert in middle
    const maxBefore = Math.max(...savedBefore);
    const minAfter = Math.min(...savedAfter);

    accessionNumber = (maxBefore + minAfter) / 2;
    // if (minAfter - maxBefore > 1) {
    //   // There's a gap, use the next integer after maxBefore
    //   accessionNumber = maxBefore + 1;
    // } else {
    //   // No gap, we need to use decimal places or shift existing numbers
    //   // For simplicity, using decimal approach
    //   accessionNumber = maxBefore + 0.5;
    // }
  }

  return accessionNumber;
};

const assignAccessionNumber = (data: MedicationDrugType) => {
  const orders = data?.medications;

  const processedMedications = data?.medications?.map((order, idx) => {
    if (isNewRow(order.status)) {
      const accessionNumber = calculateAccessionNumber(orders, idx);

      const newOrder = data?.medications?.[idx];
      newOrder.accessionNumber = accessionNumber.toString();
      orders[idx] = newOrder;
      return {
        ...order,
        accessionNumber: accessionNumber.toString(),
      };
    }
    return order;
  });

  const newMedicationsToSave = processedMedications?.filter(drug => isNewRow(drug.status));

  return newMedicationsToSave;
};

export const recalculateDurationForPastDrugs = (
  medication: MedicationDrugFormType,
  daysDurationUnit: { value: any; label: any },
) => {
  const now = new Date();
  if (!medication.scheduledDate) return medication;

  const scheduledDate = new Date(medication.scheduledDate);
  const durationValue = Number(medication.duration || 0);
  const durationUnit = medication.durationUnits?.display || '';

  const isPastScheduledOrder = scheduledDate
    ? new Date(scheduledDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
    : false;

  // ⏱️ Only recalc if scheduled date is in the past
  if (isPastScheduledOrder) {
    // Convert prescribed duration (e.g., 1 week = 7 days)
    const prescribedDays = durationValue * (durationUnitToDays[durationUnit] || 1);

    // Days between scheduled date and now
    const pastToCurrentDays = Math.max(differenceInDays(now, scheduledDate), 0);

    // Recalculate total duration = (past to current) + prescribed duration
    const totalDuration = pastToCurrentDays + prescribedDays;

    return {
      ...medication,
      duration: totalDuration,
      durationUnits: {
        uuid: daysDurationUnit?.value,
        display: daysDurationUnit?.label,
      },
    };
  }

  return medication;
};

export const recalculatePastScheduledDurations = (
  medications: MedicationDrugType['medications'],
  durationUnits: Array<Units>,
): MedicationDrugType['medications'] => {
  const now = new Date();
  const daysDurationUnit = getDefaultUnit(durationUnits, DEFAULT_DRUG_VALUES.DURATION);

  return medications.map(med => {
    if (!med.scheduledDate) return med;

    const scheduledDate = new Date(med.scheduledDate);
    const durationValue = Number(med.duration || 0);
    const durationUnit = med.durationUnits?.display || '';

    const isPastDrugOrder = scheduledDate
      ? new Date(scheduledDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
      : false;

    // ⏱️ Only recalc if scheduled date is in the past
    if (isPastDrugOrder) {
      // Convert prescribed duration (e.g., 1 week = 7 days)
      const prescribedDays = durationValue * (durationUnitToDays[durationUnit] || 1);

      // Days between scheduled date and now
      const pastToCurrentDays = Math.max(differenceInDays(now, scheduledDate), 0);

      // Recalculate total duration = (past to current) + prescribed duration
      const totalDuration = pastToCurrentDays + prescribedDays;

      return {
        ...med,
        duration: totalDuration,
        durationUnits: {
          uuid: daysDurationUnit?.value,
          display: daysDurationUnit?.label,
        },
      };
    }

    return med;
  });
};

const checkPastScheduledDate = (medications: MedicationDrugType['medications']) => {
  const pastDateActivation = medications.map(med => {
    if (med.scheduledDate) {
      const scheduledDate = new Date(med.scheduledDate);
      const now = new Date();

      // Check if scheduled date is in the past
      const isPastScheduledDate = scheduledDate < now;

      if (isPastScheduledDate) {
        const durationValue = Number(med.duration || 0);
        const durationUnit = (med.durationUnits?.display || '').toLowerCase();

        // Calculate end date
        const endDate = add(scheduledDate, {
          // hours: durationUnit.includes('hour') ? durationValue : 0,
          days: durationUnit.includes('day') ? durationValue : 0,
          weeks: durationUnit.includes('week') ? durationValue : 0,
          months: durationUnit.includes('month') ? durationValue : 0,
          years: durationUnit.includes('year') ? durationValue : 0,
        });

        // If even the end date is before now → throw error
        if (endDate < now) {
          toast.error(
            `${med?.drug?.display}: Scheduled date + duration cannot end before today. Please, increase the duration`,
          );
          return true;
        }
      }
    }
    return false;
  });

  return pastDateActivation.includes(true);
};

const parseFraction = (value: string) => {
  const cleanValue = value.trim();
  if (!cleanValue) return 0;

  if (cleanValue.includes('/')) {
    const [numeratorPart, denominatorPart] = cleanValue.split('/');
    // Extract numbers from parts (e.g., "1/2 tablet" -> "1", "2")
    const numerator = parseFloat(numeratorPart.replace(/[^0-9.]/g, ''));
    const denominator = parseFloat(denominatorPart.replace(/[^0-9.]/g, ''));
    return denominator ? numerator / denominator : 0;
  }

  // Extract number from string (e.g., "1 tablet" -> 1)
  const numericMatch = cleanValue.match(/(\d*\.?\d+)/);
  return numericMatch ? parseFloat(numericMatch[0]) : 0;
};

const isFreeTextDose = (dose: string | number) => {
  if (typeof dose !== 'string') return false;
  const trimmed = dose.trim();
  if (trimmed === '') return false;

  // Simple numeric values (e.g., "1", "0.5")
  if (!isNaN(Number(trimmed))) return false;

  // Single fractions (e.g., "1/2", "1/4") should be treated as numeric (use frequency)
  // if (/^\d+\/\d+$/.test(trimmed)) return false;

  return true;
};

const extractDoseFromFreeInstruction = (instructions: string) => {
  if (!instructions) return 1;
  return instructions.split('-').reduce((acc, i) => acc + parseFraction(i), 0);
};

const getDoseValue = (dose: number | string): number | undefined => {
  if (isFreeTextDose(dose) && typeof dose === 'string') {
    const totalDose = extractDoseFromFreeInstruction(dose);
    const activeDoseSlots = countActiveDoseSlots(dose);

    // support upto "QID"
    const divisor = activeDoseSlots > 4 ? 1 : activeDoseSlots;
    const averageDoseValue = totalDose / divisor;
    return roundUptoTwoDecimals(averageDoseValue);
  }
  return dose ? Number(dose) : undefined;
};

const pickConceptName = (names: Array<{ conceptNameType?: string; display?: string }> = [], type: string) =>
  names.find(name => name?.conceptNameType === type)?.display;

/** "SHORT (FULL)", e.g. "TAB (Tablet)", "PO (Oral)". Falls back to the full/current display when no short name exists. */
const withShortName = (names: any[] | undefined, currentDisplay?: string) => {
  const fullName = pickConceptName(names, 'FULLY_SPECIFIED') ?? currentDisplay ?? '';
  const shortName = pickConceptName(names, 'SHORT');
  return shortName ? `${shortName} (${fullName})` : fullName;
};

/**
 * Same display formatting the editable medication table applies (useMedicationActions.syncFormValues):
 * form/route as "SHORT (FULL)", dose unit as its short name, frequency as the configured label ("OD (Once a day)").
 */
const withMedicationShortDisplays = (order: Order, frequencyUnits: Array<{ label: string }> = []): Order => {
  const o: any = order;
  const frequencyLabel = frequencyUnits.find(unit => unit.label.includes(o?.frequency?.display))?.label;

  return {
    ...o,
    drug: o?.drug && {
      ...o.drug,
      dosageForm: o.drug.dosageForm && {
        ...o.drug.dosageForm,
        display: withShortName(o.drug.dosageForm.names, o.drug.dosageForm.display),
      },
    },
    route: o?.route && { ...o.route, display: withShortName(o.route.names, o.route.display) },
    doseUnits: o?.doseUnits && {
      ...o.doseUnits,
      display: pickConceptName(o.doseUnits.names, 'SHORT') ?? o.doseUnits.display,
    },
    frequency: o?.frequency && { ...o.frequency, display: frequencyLabel ?? o.frequency.display },
  };
};

export {
  // css
  customStyles,

  // algo
  assignAccessionNumber,
  calculateAccessionNumber,
  parseFraction,
  extractDoseFromFreeInstruction,

  // get constants
  createEmptyMedication,
  getDefaultDrugValues,

  // validation
  isFreeTextDose,
  isMedicationExpired,
  isEmptyValue,
  isInvalidGeneric,
  checkPastScheduledDate,
  isValidStrength,

  // find item
  findUuidByLabel,
  findConceptByDrug,
  findDuplicateMedicine,
  findDosageMappings,

  // regex
  extractTextInBrackets,

  // format fn()
  formatDrugOrder,
  formatSuggestedMedication,
  normalizeDrugOrder,
  formatMedicationsForSuggestion,
  formattedFrequencyOptions,
  sortByFrequency,
  withMedicationShortDisplays,

  // storage API
  storeMedicationToStorage,

  // math
  extractStrengthValues,
  durationUnitToDays,

  // payload preparation
  prepareMedicationPayload,
  prepareCustomGenericPayload,
  prepareCustomDrugPayload,

  // order manipulation
  getActiveDrugOrders,
  combineDrugOrders,

  // default values,
  createDefaultStrength,
};
